import { useState, useEffect } from 'react';
import { useAuth, storeUser } from '@/hooks/useAuth';
import { changeMyPassword, verifyAdminPasswordBackend } from '@/api/auth';
import { listAuditLogs } from '@/api/audit';
import { useNavigate } from 'react-router-dom';
import { broadcastCurrentAuthState } from '@/utils/authStorage';

import { updateMyProfile } from '@/api/auth';

interface AdminProfileModalProps {
  onClose: () => void;
}

export default function AdminProfileModal({ onClose }: AdminProfileModalProps) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'profile' | 'password' | 'activity'>('profile');
  const [avatarUrl, setAvatarUrl] = useState<string>(user?.avatarUrl || '');

  useEffect(() => {
    setAvatarUrl(user?.avatarUrl || '');
  }, [user?.avatarUrl]);

  const [profileForm, setProfileForm] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    company: user?.company || '',
    position: user?.position || '',
  });
  const [profileSaved, setProfileSaved] = useState(false);
  const [profileError, setProfileError] = useState('');

  const [pwdForm, setPwdForm] = useState({ current: '', next: '', confirm: '' });
  const [pwdMsg, setPwdMsg] = useState('');
  const [showPwd, setShowPwd] = useState({ current: false, next: false, confirm: false });

  const handleProfileSave = async () => {
    const trimmedPhone = profileForm.phone.trim();
    if (trimmedPhone && !/^1[3-9]\d{9}$/.test(trimmedPhone)) {
      setProfileError('手机号格式不正确');
      return;
    }

    try {
      await updateMyProfile({
        name: profileForm.name.trim(),
        phone: trimmedPhone || null,
        company: profileForm.company.trim() || null,
        position: profileForm.position.trim() || null,
      });
      // 1. 更新 auth user（登录态）
      if (user) {
        const updatedUser = {
          ...user,
          name: profileForm.name.trim(),
          phone: trimmedPhone || null,
          company: profileForm.company.trim() || null,
          position: profileForm.position.trim() || null,
        };
        storeUser(updatedUser);
        broadcastCurrentAuthState();
      }
      setProfileError('');
      setProfileSaved(true);
      setTimeout(() => setProfileSaved(false), 2500);
    } catch (err: any) {
      console.error('保存个人资料失败:', err);
      setProfileError(err?.message || '保存个人资料失败');
    }
  };

  const handlePwdSave = async () => {
    if (!pwdForm.current || !pwdForm.next || !pwdForm.confirm) {
      setPwdMsg('error:请填写所有密码字段');
      return;
    }

    try {
      const verifyRes = await verifyAdminPasswordBackend(pwdForm.current);
      if (!verifyRes.valid) {
        setPwdMsg('error:当前密码不正确');
        return;
      }
    } catch (err: any) {
      setPwdMsg('error:当前密码复核失败，请重新输入');
      return;
    }

    if (pwdForm.next.length < 8) {
      setPwdMsg('error:新密码至少 8 位');
      return;
    }
    if (!/[A-Za-z]/.test(pwdForm.next) || !/[0-9]/.test(pwdForm.next)) {
      setPwdMsg('error:新密码需包含字母和数字');
      return;
    }
    if (pwdForm.next !== pwdForm.confirm) {
      setPwdMsg('error:两次密码不一致');
      return;
    }

    try {
      await changeMyPassword({ currentPassword: pwdForm.current, newPassword: pwdForm.next });
      setPwdMsg('success:密码已成功更新，即将退出并重新登录！');
      setPwdForm({ current: '', next: '', confirm: '' });
      setTimeout(() => {
        setPwdMsg('');
        onClose();
        logout();
        navigate('/login');
      }, 1500);
    } catch (err: any) {
      setPwdMsg('error:更新密码失败：' + (err.message || '系统异常'));
    }
  };

  const [activityLogs, setActivityLogs] = useState<any[]>([]);

  useEffect(() => {
    if (activeTab === 'activity') {
      listAuditLogs({ scope: 'mine', page: 1, pageSize: 8 })
        .then((res) => {
          if (res && Array.isArray(res.list)) {
            const colors = ['#10b981', '#f97316', '#f59e0b', '#0ea5e9', '#8b5cf6', '#ef4444'];
            const icons: Record<string, string> = {
              create: 'ri-user-add-line',
              update: 'ri-edit-line',
              delete: 'ri-forbid-line',
              login: 'ri-login-box-line',
            };
            const mapped = res.list.map((log: any, idx: number) => ({
              id: log.id || idx,
              action: `${log.module}：${log.target}`,
              time: log.createdAt ? new Date(log.createdAt).toLocaleString('zh-CN', { hour12: false }) : '--',
              icon: icons[log.actionType] || 'ri-shield-check-line',
              color: colors[idx % colors.length],
            }));
            setActivityLogs(mapped);
          }
        })
        .catch((err) => console.error('Failed to load personal audit log:', err));
    }
  }, [activeTab]);


  const stats = [
    { label: '今日操作', value: '12', icon: 'ri-pulse-line', color: '#0ea5e9' },
    { label: '本周登录', value: '5', icon: 'ri-calendar-check-line', color: '#10b981' },
    { label: '管理会员数', value: '156', icon: 'ri-team-line', color: '#f97316' },
    { label: '审批通过', value: '38', icon: 'ri-checkbox-circle-line', color: '#8b5cf6' },
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[200] p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            {avatarUrl ? (
              <img src={avatarUrl} alt="avatar" className="w-12 h-12 rounded-xl object-cover shrink-0" />
            ) : (
              <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-[#0ea5e9] to-[#0284c7] rounded-xl text-white text-xl font-bold shrink-0">
                {profileForm.name.charAt(0)}
              </div>
            )}
            <div>
              <h2 className="font-bold text-gray-900">{profileForm.name || '未设置姓名'}</h2>
              <div className="flex items-center gap-1.5 mt-0.5">
                <span className="text-xs px-2 py-0.5 bg-amber-50 text-amber-600 rounded-full font-medium flex items-center gap-1">
                  <i className="ri-vip-crown-fill text-amber-500 text-xs"></i>超级管理员
                </span>
                <span className="text-xs text-gray-400">{profileForm.email}</span>
              </div>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer">
            <i className="ri-close-line text-gray-500"></i>
          </button>
        </div>

        {/* Stats */}
        <div className="px-6 py-4 grid grid-cols-4 gap-3 border-b border-gray-100 shrink-0">
          {stats.map((s) => (
            <div key={s.label} className="text-center p-3 bg-gray-50 rounded-xl">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg mx-auto mb-1.5" style={{ backgroundColor: `${s.color}15` }}>
                <i className={`${s.icon} text-sm`} style={{ color: s.color }}></i>
              </div>
              <div className="text-lg font-bold text-gray-900">{s.value}</div>
              <div className="text-xs text-gray-400">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="px-6 pt-3 flex gap-1 bg-gray-50 mx-6 rounded-xl shrink-0">
          {([
            { key: 'profile', label: '个人信息', icon: 'ri-user-line' },
            { key: 'password', label: '修改密码', icon: 'ri-lock-line' },
            { key: 'activity', label: '操作记录', icon: 'ri-history-line' },
          ] as const).map((t) => (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              className={`flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${activeTab === t.key ? 'bg-white text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
            >
              <i className={`${t.icon} text-sm`}></i>{t.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'profile' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">姓名</label>
                  <input type="text" value={profileForm.name} onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#0ea5e9]" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">邮箱</label>
                  <input type="email" value={profileForm.email} disabled
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm bg-gray-50 text-gray-400 cursor-not-allowed" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">手机号码</label>
                  <input type="text" value={profileForm.phone} onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#0ea5e9]" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">公司/部门</label>
                  <input type="text" value={profileForm.company} onChange={(e) => setProfileForm({ ...profileForm, company: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#0ea5e9]" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">职位</label>
                  <input type="text" value={profileForm.position} onChange={(e) => setProfileForm({ ...profileForm, position: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#0ea5e9]" />
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button onClick={handleProfileSave} className="px-6 py-2.5 bg-[#0ea5e9] text-white text-sm font-medium rounded-xl hover:bg-[#0284c7] cursor-pointer whitespace-nowrap">保存修改</button>
                {profileSaved && <span className="text-[#10b981] text-sm flex items-center gap-1.5"><i className="ri-checkbox-circle-line"></i>信息已保存！</span>}
                {profileError && <span className="text-red-500 text-sm flex items-center gap-1.5"><i className="ri-error-warning-line"></i>{profileError}</span>}
              </div>
            </div>
          )}

          {activeTab === 'password' && (
            <div className="max-w-sm space-y-4">
              <div className="p-3 bg-amber-50 border border-amber-100 rounded-xl text-xs text-amber-700 flex items-start gap-2">
                <i className="ri-information-line mt-0.5 shrink-0"></i>
                <span>新密码需含字母和数字，至少 8 位。修改后立即生效，升级管理员、赠送灵石等所有复核操作均需使用新密码。</span>
              </div>
              {([
                { label: '当前密码', key: 'current' as const },
                { label: '新密码', key: 'next' as const },
                { label: '确认新密码', key: 'confirm' as const },
              ]).map((f) => (
                <div key={f.key}>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">{f.label}</label>
                  <div className="relative">
                    <input
                      type={showPwd[f.key] ? 'text' : 'password'}
                      value={pwdForm[f.key]}
                      onChange={(e) => setPwdForm({ ...pwdForm, [f.key]: e.target.value })}
                      placeholder={`请输入${f.label}`}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#0ea5e9] pr-10"
                    />
                    <button type="button" onClick={() => setShowPwd((p) => ({ ...p, [f.key]: !p[f.key] }))}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer">
                      <i className={`${showPwd[f.key] ? 'ri-eye-off-line' : 'ri-eye-line'} text-sm`}></i>
                    </button>
                  </div>
                </div>
              ))}
              {pwdMsg && (
                <div className={`text-sm flex items-start gap-1.5 ${pwdMsg.startsWith('error') ? 'text-red-500' : 'text-[#10b981]'}`}>
                  <i className={`${pwdMsg.startsWith('error') ? 'ri-error-warning-line' : 'ri-checkbox-circle-line'} shrink-0 mt-0.5`}></i>
                  <span>{pwdMsg.replace(/^(error|success):/, '')}</span>
                </div>
              )}
              <button onClick={handlePwdSave} className="px-6 py-2.5 bg-[#0ea5e9] text-white text-sm font-medium rounded-xl hover:bg-[#0284c7] cursor-pointer whitespace-nowrap">
                更新密码
              </button>
            </div>
          )}

          {activeTab === 'activity' && (
            <div className="space-y-2">
              {activityLogs.map((log) => (
                <div key={log.id} className="flex items-center gap-3 p-3.5 bg-gray-50 rounded-xl hover:bg-gray-100 transition-all">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg shrink-0" style={{ backgroundColor: `${log.color}15` }}>
                    <i className={`${log.icon} text-sm`} style={{ color: log.color }}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800">{log.action}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{log.time}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
